import {
  Box,
  Paper,
  Table,
  Tooltip,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  IconButton,
  TableSortLabel,
  TableContainer,
  TablePagination,
  Skeleton,
} from '@mui/material';
import { IColumnDefinition } from '@common/types';
import { theme } from '@store';
import { usePreviewTable } from './hooks';
import { IPreviewTableEntry, IPreviewTableFilters } from '../../../types.ts';
import { useRef, useMemo } from 'react';
import { OpenInNew } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { getDeepProperty } from '@common/types/utilities.ts';
import { isArray } from 'lodash';
import { reportingTableRowNameFormat } from '@util/reportingTableRowNameFormat.ts';
import {
  TableNoResultPlaceholder,
  TruncatedTextWithConditionalTooltip,
} from '@components';

interface IPreviewTableProps {
  rows: IPreviewTableEntry[];
  columns: IColumnDefinition[];
  filters: IPreviewTableFilters;
  setFilters: (filters: IPreviewTableFilters) => void;
  totalCount: number;
  loading: boolean;
  type?: 'patients' | 'tasks' | 'records' | 'users';
  //This props determines whether this table will render under a collapsible component,
  //as a subtable inside another table
  displayHead?: boolean;
  rowOnClick?: (id: string) => void;
  footerWidth?: string | number;
}

export const PreviewTable = ({
  rows,
  filters,
  columns,
  setFilters,
  totalCount,
  loading,
  type,
  displayHead = true,
  rowOnClick,
  footerWidth,
}: IPreviewTableProps) => {
  const { sort_by, sort_order } = filters;
  const { onPageSizeChange, handleChangeOrder, onPageIndexChange } =
    usePreviewTable({ filters, setFilters });

  const stableCountRef = useRef(totalCount);
  const stableCount = useMemo(() => {
    if (!loading) {
      stableCountRef.current = totalCount;
    }
    return stableCountRef.current;
  }, [loading, totalCount]);

  const cellStyle = {
    width: '300px',
    minWidth: '300px',
    maxWidth: '300px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'left' as const,
    paddingY: '1rem',
  };

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 0,
          boxShadow: 'none',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderBottom: 'none',
          maxHeight: `calc(100vh - 480px)`,
        }}
      >
        <Table stickyHeader size='small'>
          {displayHead && (
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: theme.palette.grey[200]!,
                }}
              >
                {columns.map((col, index) => (
                  <TableCell
                    key={`${col.id}-${index}`}
                    sx={{
                      ...cellStyle,
                      bgcolor: theme.palette.grey[200],
                    }}
                  >
                    {col.sortable ? (
                      <TableSortLabel
                        active={true}
                        direction={
                          col.id === sort_by
                            ? (sort_order?.toLowerCase() as 'asc' | 'desc')
                            : 'asc'
                        }
                        onClick={() =>
                          handleChangeOrder(
                            col.id as IPreviewTableFilters['sort_by'],
                          )
                        }
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          '& .MuiTableSortLabel-icon': { ml: 1, flexShrink: 0 },
                        }}
                      >
                        <TruncatedTextWithConditionalTooltip
                          text={col.label}
                          width='100%'
                          variant='body2'
                        />
                      </TableSortLabel>
                    ) : (
                      <TruncatedTextWithConditionalTooltip
                        text={col.label}
                        width='100%'
                        variant='body2'
                      />
                    )}
                  </TableCell>
                ))}
                {type === 'patients' && (
                  <TableCell
                    sx={{
                      ...cellStyle,
                      width: 75,
                      minWidth: 75,
                      maxWidth: 75,
                      textAlign: 'center',
                      bgcolor: theme.palette.grey[200],
                    }}
                  />
                )}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {loading
              ? Array.from({ length: filters.limit ?? 10 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {columns.map(col => (
                      <TableCell key={col.id} sx={{ ...cellStyle }}>
                        <Skeleton variant='text' width='80%' />
                      </TableCell>
                    ))}
                    {type === 'patients' && (
                      <TableCell
                        sx={{
                          ...cellStyle,
                          width: 75,
                          minWidth: 75,
                          maxWidth: 75,
                        }}
                      >
                        <Skeleton
                          variant='circular'
                          width={24}
                          height={24}
                          sx={{ mx: 'auto' }}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))
              : rows.map((row, index) => (
                  <TableRow
                    key={`${row.id}-${index}`}
                    sx={{
                      cursor: rowOnClick ? 'pointer' : 'auto',
                      ...(rowOnClick && {
                        '&:hover, &:nth-of-type(even):hover': {
                          backgroundColor: theme.palette.hoverTableRowBg.main,
                        },
                      }),
                    }}
                    onClick={() => {
                      if (rowOnClick && row.id) {
                        rowOnClick(row.id);
                      }
                    }}
                  >
                    {columns.map(col => {
                      const val = getDeepProperty(row, col.id);

                      const formattedValue = isArray(val)
                        ? val
                            .map(item => reportingTableRowNameFormat(item))
                            .join(', ')
                        : reportingTableRowNameFormat(val as string | boolean);
                      return (
                        <TableCell key={col.id} sx={{ ...cellStyle }}>
                          <Tooltip
                            title={val?.toString()}
                            enterDelay={150}
                            leaveDelay={0}
                          >
                            <span>{formattedValue}</span>
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                    {type === 'patients' && (
                      <TableCell
                        sx={{
                          ...cellStyle,
                          width: 75,
                          minWidth: 75,
                          maxWidth: 75,
                          textAlign: 'center',
                        }}
                      >
                        <Link to={`/patient/${row.id}`}>
                          <IconButton>
                            <OpenInNew sx={{ width: 24, height: 24 }} />
                          </IconButton>
                        </Link>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length === 0 && !loading && (
        <Box
          sx={{
            bgcolor: theme.palette.common.white,
            display: 'grid',
            placeItems: 'center',
            width: '100%',
            height: 'calc(100vh - 520px)',
            borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <TableNoResultPlaceholder />
        </Box>
      )}
      {displayHead ? (
        <TablePagination
          sx={{
            bgcolor: theme.palette.common.white,
            paddingY: '0.75rem',
            zIndex: 100,
            border: '1px solid rgba(0, 0, 0, 0.12)',
            position: 'sticky',
            bottom: 0,
            left: 0,
            width: footerWidth,
          }}
          component='div'
          count={stableCount}
          page={filters.page! - 1}
          rowsPerPage={filters.limit!}
          onPageChange={onPageIndexChange}
          onRowsPerPageChange={onPageSizeChange}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            bgcolor: theme.palette.grey[50],
            border: '1px solid rgba(0, 0, 0, 0.12)',
            position: 'sticky',
            bottom: 0,
            left: 0,
            width: footerWidth,
          }}
        >
          <TablePagination
            component='div'
            count={stableCount}
            page={filters.page! - 1}
            rowsPerPage={filters.limit!}
            onPageChange={onPageIndexChange}
            onRowsPerPageChange={onPageSizeChange}
            sx={{
              paddingY: '0.75rem',
              width: 'auto',
            }}
          />
        </Box>
      )}
    </>
  );
};
